/**
 * Extends the global Window interface to include Powerfront-specific properties.
 *
 * @typedef {Object} Window
 * @property {Array<any>} _inside - Array for Powerfront chat configuration
 * @property {Object} insideFrontInterface - Interface for Powerfront chat interactions
 */
declare global {
  interface Window {
    _inside: Array<any>
    insideFrontInterface: any
  }
}

/**
 * Nuxt plugin for integrating Powerfront live chat functionality.
 *
 * This plugin sets up the Powerfront chat interface, manages chat availability,
 * and handles dynamic chat initialization. The vendor script is loaded only once
 * OneTrust has initialised (its cookies are strictly necessary, category C0001),
 * or immediately on locales that never show the cookie banner.
 *
 * @name powerfront
 * @returns {void}
 * @example
 * // Chat availability is tracked via insideChat reactive state
 * // Automatically opens chat pane if requested before initialization
 */
export default defineNuxtPlugin({
  name: 'powerfront',
  setup() {
    const { insideChat } = useInsideChat()

    /**
     * Configures the Powerfront chat setup and availability tracking.
     *
     * @function setupChat
     * @description Initializes chat configuration and sets up availability callback
     */
    const setupChat = () => {
      window._inside ||= []

      if (window._inside && Array.isArray(window._inside))
        window._inside.push({
          action: 'bind',
          name: 'chatavailable',
          /**
           * Callback to handle chat availability status.
           *
           * @param {boolean} available - Indicates if chat is currently available
           */
          callback: (available: boolean) => {
            try {
              insideChat.value.isLoaded = true
              insideChat.value.isAvailable = available

              if (available && insideChat.value.hasBeenRequestedBeforeInit) {
                // @ts-ignore
                window.insideFrontInterface.openChatPane()
              }
            } catch (error) {
              console.error('Error in livechat callback: ', error)
            }
          },
        })
    }

    setupChat()

    const insideScript = useScript(
      {
        async: true,
        defer: true,
        src: 'https://cdn8.eu.inside.chat/gtm/IN-1001177-NEW/include.js',
      },
      { trigger: 'manual' }
    )

    // Inside cookies are strictly necessary (C0001): load the script once OneTrust has
    // initialised, or immediately on locales that never show the banner (AMS-737, point 8).
    useUserCookieConsent({
      categories: ['C0001'],
      onFirstAccept: () => insideScript.load(),
    })
  },
})
